import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAccessToken } from "@/hooks";
import { ClassEnrollmentsApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

export const useClassesEnrollmentsByStudentIdsGet = (
  studentIds: number[],
  options?: { refetchOnMount?: boolean | "always" },
) => {
  const accessToken = useAccessToken();
  const refetchOnMount = options?.refetchOnMount ?? false;

  // Stabilize studentIds array to prevent unnecessary re-renders
  // Create a stable string key that only changes when IDs actually change
  // Since studentIds is already memoized in components that use this hook,
  // we can use it directly but still create a stable key for the query
  const studentIdsKey = useMemo(() => {
    if (!studentIds || studentIds.length === 0) return "";
    const uniqueSorted = [...new Set(studentIds)].sort((a, b) => a - b);
    return uniqueSorted.join(",");
  }, [studentIds]);
  
  // Stabilize enabled flag to prevent query from being enabled/disabled repeatedly
  // which could cause infinite loops in production
  const enabled = useMemo(() => {
    return studentIds.length > 0 && !!accessToken;
  }, [studentIds.length, accessToken]);
  
  const { data, isError, isSuccess, isPending, refetch } = useQuery({
    queryKey: [
      QueryKeys.ClassesEnrollmentsByStudentId,
      "multiple",
      studentIdsKey,
    ],
    queryFn: () => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(ClassEnrollmentsApi, accessToken);
      return service.apiClassEnrollmentsStudentsGet({
        ids: studentIds.length > 0 ? studentIds : undefined,
        page: 1,
        pageSize: 1000, // Get all enrollments
      });
    },
    enabled,
    refetchOnMount,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    data,
    isError,
    isSuccess,
    isPending,
    refetch,
  };
};
